import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Gift, DollarSign, Percent, Clock, Tag, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';



const INCENTIVE_ICONS = {
  cash_back: DollarSign,
  rate_discount: Percent,
  closing_cost_credit: Tag,
  bonus: Gift
};

const INCENTIVE_COLORS = {
  cash_back: 'bg-green-500/10 text-green-400 border-green-500/20',
  rate_discount: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  closing_cost_credit: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  bonus: 'bg-orange-500/10 text-orange-400 border-orange-500/20'
};

const LenderIncentives = () => {
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    fetchIncentives();
  }, []);
  
  const fetchIncentives = async () => {
    try {
      const response = await api.get(`/api/incentives`);
      setIncentives(response.data.incentives || []);
    } catch (error) {
      console.error('Error fetching incentives:', error);
      toast.error('Failed to load incentives');
    } finally {
      setLoading(false);
    }
  };
  
  const formatValue = (incentive) => {
    if (incentive.value_type === 'percentage') {
      return `${incentive.value}%`;
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(incentive.value);
  };
  
  const getDaysUntilExpiry = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const getExpiryStatus = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days <= 7) return { color: 'text-red-400', label: 'Expires Soon' };
    if (days <= 30) return { color: 'text-yellow-400', label: 'Ending Soon' };
    return { color: 'text-green-400', label: 'Active' };
  };
  
  const filteredIncentives = incentives.filter(inc => {
    if (filter === 'all') return true;
    return inc.incentive_type === filter;
  });
  
  const incentiveTypes = [...new Set(incentives.map(i => i.incentive_type))];
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="lender-incentives" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-heading font-semibold">Current Lender Incentives</h3>
        </div>
        <span className="text-sm text-muted-foreground">
          {incentives.length} active offers
        </span>
      </div>
      
      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
            filter === 'all' 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          All ({incentives.length})
        </button>
        {incentiveTypes.map(type => {
          const count = incentives.filter(i => i.incentive_type === type).length;
          const Icon = INCENTIVE_ICONS[type] || Gift;
          return (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filter === type 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} ({count})
            </button>
          );
        })}
      </div>
      
      {/* Incentive Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredIncentives.map((incentive) => {
          const Icon = INCENTIVE_ICONS[incentive.incentive_type] || Gift;
          const colorClasses = INCENTIVE_COLORS[incentive.incentive_type] || INCENTIVE_COLORS.bonus;
          const expiryStatus = getExpiryStatus(incentive.expiry_date);
          const isExpanded = expandedId === incentive.incentive_id;
          const daysLeft = getDaysUntilExpiry(incentive.expiry_date);
          
          return (
            <div 
              key={incentive.incentive_id}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors"
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg border ${colorClasses}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{incentive.lender}</p>
                      <p className="text-sm text-muted-foreground">{incentive.title}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold font-mono text-primary">
                      {formatValue(incentive)}
                    </p>
                    <span className={`text-xs ${colorClasses.split(' ')[1]}`}>
                      {incentive.incentive_type.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground mb-3">
                  {incentive.description}
                </p>
                
                {/* Expiry & Requirements */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <Clock className={`h-4 w-4 ${expiryStatus.color}`} />
                    <span className={expiryStatus.color}>
                      {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
                    </span>
                  </div>
                  
                  {(incentive.min_mortgage || incentive.max_mortgage) && (
                    <span className="text-muted-foreground">
                      {incentive.min_mortgage && `Min: $${(incentive.min_mortgage/1000).toFixed(0)}K`}
                      {incentive.min_mortgage && incentive.max_mortgage && ' • '}
                      {incentive.max_mortgage && `Max: $${(incentive.max_mortgage/1000).toFixed(0)}K`}
                    </span>
                  )}
                </div>
                
                {/* Expand/Collapse Button */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : incentive.incentive_id)}
                  className="flex items-center gap-1 mt-3 text-sm text-primary hover:underline"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      View Terms
                    </>
                  )}
                </button>
              </div>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-border bg-muted/20">
                  <div className="space-y-2 text-sm">
                    {incentive.terms && (
                      <div>
                        <p className="font-medium mb-1">Terms & Conditions:</p>
                        <p className="text-muted-foreground">{incentive.terms}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-xs text-muted-foreground">
                        Source: {incentive.source}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Expires: {new Date(incentive.expiry_date).toLocaleDateString('en-CA')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {filteredIncentives.length === 0 && (
        <div className="text-center py-8">
          <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No incentives found for this filter.</p>
        </div>
      )}
      
      {/* Disclaimer */}
      <div className="p-4 bg-muted/30 border border-border rounded-xl text-xs text-muted-foreground">
        <p className="font-medium text-foreground mb-1">Disclaimer</p>
        <p>
          Incentive offers are subject to change and may have additional eligibility requirements. 
          Always verify current offers directly with the lender before making decisions. 
          Some incentives may not be combinable with other offers.
        </p>
      </div>
    </div>
  );
};

export default LenderIncentives;
